import { FormProvider, useForm } from 'react-hook-form'
import { CompanyDetail } from 'components/molecules/CompanyDetail'
import { SalesPersons } from 'components/molecules/SalesPersons'
import { Memo } from 'components/atoms/inputs/Memo'
import { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import swal from 'sweetalert2'
import { AuthState } from 'recoil/atoms/auth.atom'
import { useSelectCompany } from 'api/company/useSelectCompany'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateCompany } from 'api/company/updateCompany'
import { deleteCompany } from 'api/company/deleteCompany'
import {
  CompanyUpdateSchema,
  CompanyUpdateSchemaType
} from 'schema/company/CompanyUpdateSchema'
import { dateToInputForm } from 'utils/dateToString'

type Props = {
  companyId: string
}

export const CompanyDetailForm = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const [auth] = useRecoilState(AuthState)
  const { data: company, mutate } = useSelectCompany(companyId)
  const [oldMemo, setOldMemo] = useState('')
  const methods = useForm<CompanyUpdateSchemaType>({
    resolver: zodResolver(CompanyUpdateSchema)
  })

  useEffect(() => {
    if (company) {
      methods.reset({
        ...company,
        frequencyCode: company.frequencyCode ?? 'NONE',
        tagCode: company.tagCode ?? 'NONE',
        specialty: company.specialty ?? '',
        specialtyIndustry: company.specialtyIndustry ?? '',
        postCode: company.postCode ?? '',
        address: company.address ?? '',
        homePhone: company.homePhone ?? '',
        homepage: company.homepage ?? '',
        establishment: dateToInputForm(company.establishment ?? ''),
        invoice: company.invoice ?? '',
        deliverAddress: company.deliverAddress ?? '',
        dispatchLicenseNumber: company.dispatchLicenseNumber ?? '',
        memo: '',
        focusCustomer: company.focusCustomer ?? false,
        sales: company.sales.map((sales) => ({
          ...sales,
          fullName: [sales.lastName, sales.firstName].join(' ')
        }))
      })
      setOldMemo(company.memo || '')
    }
  }, [company, methods])

  const onSubmit = async (data: CompanyUpdateSchemaType) => {
    if (company) {
      const convertSales = data.sales.map((salesUser) => {
        const splitName = salesUser.fullName.split(' ')
        return {
          lastName: splitName[0],
          firstName: splitName[1],
          mail: salesUser.mail,
          department: salesUser.department || undefined,
          cellPhone: salesUser.cellPhone || undefined,
          post: salesUser.post || undefined
        }
      })
      const request = {
        ...data,
        id: company.id,
        version: company.version,
        sales: convertSales
      }
      await updateCompany(request)
        .then(() => {
          swal
            .fire({
              icon: 'info',
              text: '会社情報を更新しました。',
              confirmButtonText: 'OK'
            })
            .then((result) => {
              if (result.isConfirmed) {
                mutate()
              }
            })
        })
        .catch((error: any) => {
          swal.fire({
            icon: 'error',
            text: error?.data?.message || '会社情報の更新に失敗しました。',
            confirmButtonText: 'OK'
          })
        })
    }
  }

  const deleteHandler = async () => {
    if (!company) return
    swal
      .fire({
        icon: 'info',
        text: '会社情報を削除しますか？',
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        showCancelButton: true
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteCompany({
            id: company.id,
            version: company.version
          })
            .then(() => {
              swal
                .fire({
                  icon: 'info',
                  text: '会社情報を削除しました。',
                  confirmButtonText: 'OK'
                })
                .then((deleteResult) => {
                  if (deleteResult.isConfirmed) {
                    navigate('/company')
                  }
                })
            })
            .catch((error: any) => {
              swal.fire({
                icon: 'error',
                text: error?.data?.message || '会社情報の削除に失敗しました。',
                confirmButtonText: 'OK'
              })
            })
        }
      })
  }

  return company ? (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
        <div
          className={
            'rounded-lg bg-white p-6 shadow-[0_2px_4px_rgba(0,0,0,0.08)]'
          }
        >
          <div className={'grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]'}>
            <div>
              <CompanyDetail updateAt={company?.updateAt} />
              <SalesPersons />
              <div className={'mt-8 flex justify-center gap-3'}>
                {auth === 'ADMIN' && (
                  <button
                    type={'button'}
                    onClick={deleteHandler}
                    className={
                      'rounded-md border border-[#ddd] bg-white px-8 py-2.5 text-sm font-medium text-[#666] transition-colors hover:bg-[#f8f9fa]'
                    }
                  >
                    削除
                  </button>
                )}
                <button
                  type={'submit'}
                  className={
                    'rounded-md bg-[#01695C] px-8 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#015a4e]'
                  }
                >
                  更新
                </button>
              </div>
            </div>
            <div
              className={'sticky top-[100px] h-fit rounded-lg bg-[#f8f9fa] p-5'}
            >
              <div className={'mb-3 text-sm font-semibold text-[#666]'}>
                メモ
              </div>
              <Memo
                register={methods.register('memo')}
                placeHolder={'先方担当者：\n注力情報：\n業界：'}
              />
              <Memo value={oldMemo} disabled />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  ) : null
}
